import React, { RefObject, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import EditableVisibleName from "./EditableVisibleName";
import "../styles/ChatMenu.css";

type Props = {
  isOpen: boolean;
  onClose: () => void;

  editingName: boolean;
  visibleName: string;
  draftName: string;
  nameContainerRef: RefObject<HTMLDivElement>;
  onDraftNameChange: (value: string) => void;
  onStartEditing: () => void;
  onSave: () => void;
  onCancelEditing: () => void;

  onOpenApp: () => void;
  onOpenStore: () => void;
};

type OpenSection = "name" | "getApp" | "saveChat" | null;

function AccordionChevron({ isOpen }: { isOpen: boolean }) {
  return (
    <svg
      className="chat-menu-chevron-icon"
      viewBox="0 0 24 24"
      aria-hidden="true"
      focusable="false"
    >
      <path d={isOpen ? "M18 15l-6-6-6 6" : "M6 9l6 6 6-6"} />
    </svg>
  );
}

const ChatMenu: React.FC<Props> = ({
  isOpen,
  onClose,

  editingName,
  visibleName,
  draftName,
  nameContainerRef,
  onDraftNameChange,
  onStartEditing,
  onSave,
  onCancelEditing,

  onOpenApp,
  onOpenStore,
}) => {
  const modalRef = useRef<HTMLDivElement>(null);
  const [openSection, setOpenSection] = useState<OpenSection>(null);

  useEffect(() => {
    if (!isOpen) {
      setOpenSection(null);
      onCancelEditing();
    }
  }, [isOpen, onCancelEditing]);

  useEffect(() => {
    if (!isOpen || !editingName) return;

    const handlePointerDown = (event: MouseEvent | TouchEvent) => {
      const target = event.target as Node | null;
      if (!target) return;

      if (modalRef.current?.contains(target)) return;

      onCancelEditing();
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("touchstart", handlePointerDown);

    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("touchstart", handlePointerDown);
    };
  }, [isOpen, editingName, onCancelEditing]);

  const closeMenu = () => {
    onCancelEditing();
    setOpenSection(null);
    onClose();
  };

  const toggleSection = (section: OpenSection) => {
    onCancelEditing();

    setOpenSection((current) => {
      if (current === section) return null;
      return section;
    });
  };

  if (!isOpen) return null;

  return createPortal(
    <div className="chat-menu-backdrop" onClick={closeMenu}>
      <div
        ref={modalRef}
        className="chat-menu-modal"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="chat-menu-header">
          <div>
            <div className="chat-menu-title">Chat settings</div>
            <div className="chat-menu-subtitle">Manage this chat</div>
          </div>

          <button className="chat-menu-close" onClick={closeMenu}>
            ×
          </button>
        </div>

        <div className="chat-menu-accordion">
          <button
            className={`chat-menu-row ${
              openSection === "name" ? "chat-menu-row-open" : ""
            }`}
            type="button"
            aria-expanded={openSection === "name"}
            onClick={() => toggleSection("name")}
          >
            <span>Edit Name</span>
            <span className="chat-menu-chevron" aria-hidden="true">
              <AccordionChevron isOpen={openSection === "name"} />
            </span>
          </button>

          {openSection === "name" && (
            <div className="chat-menu-expanded">
              <div className="chat-menu-card-subtitle">
                This is how you appear in this chat.
              </div>

              <EditableVisibleName
                editingName={editingName}
                visibleName={visibleName}
                draftName={draftName}
                nameContainerRef={nameContainerRef}
                onDraftNameChange={onDraftNameChange}
                onStartEditing={onStartEditing}
                onSave={onSave}
                onCancel={onCancelEditing}
                variant="menu"
              />
            </div>
          )}

          <button
            className={`chat-menu-row ${
              openSection === "getApp" ? "chat-menu-row-open" : ""
            }`}
            type="button"
            aria-expanded={openSection === "getApp"}
            onClick={() => toggleSection("getApp")}
          >
            <span>Get App</span>
            <span className="chat-menu-chevron" aria-hidden="true">
              <AccordionChevron isOpen={openSection === "getApp"} />
            </span>
          </button>

          {openSection === "getApp" && (
            <div className="chat-menu-expanded">
              <div className="chat-menu-card-subtitle">
                Install IDless to continue chats on your phone.
              </div>

              <button
                className="chat-menu-action"
                type="button"
                onClick={onOpenStore}
              >
                Get from Store
              </button>
            </div>
          )}

          <button
            className={`chat-menu-row ${
              openSection === "saveChat" ? "chat-menu-row-open" : ""
            }`}
            type="button"
            aria-expanded={openSection === "saveChat"}
            onClick={() => toggleSection("saveChat")}
          >
            <span>Save Chat</span>
            <span className="chat-menu-chevron" aria-hidden="true">
              <AccordionChevron isOpen={openSection === "saveChat"} />
            </span>
          </button>

          {openSection === "saveChat" && (
            <div className="chat-menu-expanded">
              <div className="chat-menu-card-subtitle">
                This chat ends when the session expires. Open it in IDless to
                keep it. Need the app first? Use Get App.
              </div>

              <button
                className="chat-menu-action"
                type="button"
                onClick={onOpenApp}
              >
                Open IDless
              </button>
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default ChatMenu;
